// EV IQ shared constants
// APP_URL is the in-product mobile app experience (React route within this app).
// Kept as a relative path so it opens in the same tab and stays inside Emergent.
export const APP_URL = "/app";

// Google Business Profile review link — leave empty until the profile
// exists; every "leave a review" surface auto-hides while this is blank.
export const GOOGLE_REVIEW_URL = "";

// Payment links (Stripe hosted checkout, owner's Stripe account).
// Payments are auto-verified via the /api/webhook/stripe-links webhook —
// no manual approval step required.
export const CHECKOUT_URLS = {
  basic: "https://buy.stripe.com/7sYfZhcTnfQMdGg8KScZa00",
  yearly: "https://buy.stripe.com/eVq14n5qVfQM59Kd18cZa01",
  lifetime: "https://buy.stripe.com/6oU00j2eJcEA0TugdkcZa02",
};

// Fallback route/mailto used when a checkout URL is not yet configured.
export const CHECKOUT_FALLBACK = "/contact?topic=lifetime";

// Public support email used across CTAs and post-payment instructions.
export const SUPPORT_EMAIL = "support@eviq.pro";

// Free preview window length. Every new visitor gets 7 days of full-feature
// access; after that we hard-gate the app and require a plan choice.
export const PREVIEW_DAYS = 7;

/**
 * Which tiers unlock which features. A tier is one of:
 *   "preview" — free 7-day window (unlimited access while it lasts)
 *   "basic"   — $9.99/mo
 *   "yearly"  — $99.99/yr    ← this is where the differentiators land
 *   "lifetime"— $199.99 one-time
 *   null      — expired preview / no subscription
 */
export const PREMIUM_TIERS = ["yearly", "lifetime", "preview"];

// Slim, decision-focused navigation. The old nav had 10+ items and read as
// a table-of-contents; this collapses to the four decisions a visitor
// actually makes on this site.
export const NAV_LINKS = [
  { id: "features",       label: "Features",  tKey: "features" },
  { id: "pricing",        label: "Pricing",   tKey: "pricing" },
  { id: "blog",           label: "Blog",      tKey: "blog" },
  { id: "faq",            label: "FAQ",       tKey: "faq" },
];

// Secondary links that show under the "More" desktop menu / mobile drawer.
// Kept for discoverability without cluttering the top bar.
export const NAV_SECONDARY_LINKS = [
  { id: "four-pillars",   label: "Four Pillars",  tKey: "four_pillars" },
  { id: "how-it-works",   label: "How it works",  tKey: "how_it_works" },
  { id: "demo",           label: "Live demo",     tKey: "demo" },
];

export const FEATURES = [
  {
    icon: "BatteryCharging",
    title: "Real-time battery health",
    desc: "Live State-of-Health percentage pulled directly from your car every session — no estimates, no guesswork.",
    accent: "green",
  },
  {
    icon: "ShieldCheck",
    title: "Warranty threshold alerts",
    desc: "Every OEM has a capacity floor — Tesla's is 70% within 8 years / 150,000 mi. We watch your model's line so you never miss the claim window.",
    accent: "cyan",
  },
  {
    icon: "TrendingDown",
    title: "Degradation curve tracking",
    desc: "Historical charts show your pack's degradation against mileage, calendar age, and the fleet average for your model.",
    accent: "green",
  },
  {
    icon: "Zap",
    title: "Charging efficiency analysis",
    desc: "Spot inefficient sessions, vampire drain, and Supercharger vs. home AC differences across thousands of data points.",
    accent: "cyan",
  },
  {
    icon: "Gauge",
    title: "Estimated range loss",
    desc: "See how many real-world miles you've lost — at 100% charge, 80% daily, and projected across your warranty window.",
    accent: "green",
  },
  {
    icon: "Lock",
    title: "OAuth + AES-256 encryption",
    desc: "Read-only manufacturer OAuth scope. Tokens encrypted at rest. No passwords stored. Revoke access anytime.",
    accent: "cyan",
  },
];

export const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Sign in with your manufacturer",
    desc: "Use your manufacturer's official OAuth — Tesla, Ford, or Volvo/Polestar. Your credentials go directly to them; EV IQ only receives a limited-scope read-only token.",
  },
  {
    step: "02",
    title: "We sync your vehicle data",
    desc: "Battery level, charge state, range, and odometer are pulled every session — encrypted, anonymized, and stored at rest.",
  },
  {
    step: "03",
    title: "Get clear insights",
    desc: "Your health score, degradation curve, and warranty headroom are computed and displayed in plain English.",
  },
  {
    step: "04",
    title: "Get alerted before it matters",
    desc: "The moment you cross your model's warranty line — like Tesla's 70% floor — we send you a notification, and a one-page report ready for the service center.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "EV IQ caught my Model S Plaid degrading 1.4% in 60 days. Tesla swapped the pack under warranty. The report from EV IQ was what closed the case.",
    name: "Marcus Chen",
    role: "Model S Plaid · 2022",
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MjE1MTQ1OHww&ixlib=rb-4.1.0&q=85",
  },
  {
    quote:
      "I check EV IQ every Sunday with my coffee. The degradation chart vs. fleet average is genuinely the only number I trust about my battery.",
    name: "Priya Anand",
    role: "Model 3 LR · 2021",
    avatar:
      "https://images.unsplash.com/photo-1607503873903-c5e95f80d7b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MjE1MTQ1OHww&ixlib=rb-4.1.0&q=85",
  },
  {
    quote:
      "Bought a used Model Y. EV IQ told me within 48 hours that the previous owner had been Supercharging it daily. Saved me from a bad resale next year.",
    name: "Daniel Okafor",
    role: "Model Y · 2020",
    avatar:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4MjE1MTQ1OHww&ixlib=rb-4.1.0&q=85",
  },
];

export const PRICING = [
  {
    name: "Basic",
    price: "$9.99",
    period: "/month",
    savings: "Cancel anytime by email",
    headline: "Everyday battery visibility for one vehicle — less than one Supercharger session a month.",
    features: [
      "1 vehicle",
      "Live State-of-Health monitoring",
      "Battery Intelligence Passport — certify & share",
      "30-day degradation slope",
      "Warranty threshold tracking",
      "Recall check + new-recall email alerts",
      "Basic anomaly detection",
      "Email alerts",
    ],
    cta: "Get Basic",
    plan: "basic",
    highlight: false,
  },
  {
    name: "Yearly",
    price: "$99.99",
    period: "/year",
    savings: "Save 17% with annual — $99.99 vs $119.88 monthly",
    headline: "The full EVIQ toolkit. Warranty PDF, AI, lifespan forecast.",
    features: [
      "Everything in Basic",
      "Battery Life Predictor · exclusive",
      "AI narrative & insights · exclusive",
      "Service-ready warranty PDF · exclusive",
      "Recall Compliance Report (seller PDF) · exclusive",
      "Full 5-class anomaly detection",
      "Cohort & fleet benchmarking",
      "Up to 3 vehicles",
      "Priority email support",
    ],
    cta: "Go Yearly",
    plan: "yearly",
    highlight: true,
  },
  {
    name: "Lifetime",
    price: "$199.99",
    period: "one-time",
    savings: "Pay once · never again",
    headline: "One-time, less than a single tire rotation — covers 5 vehicles forever.",
    features: [
      "Everything in Yearly",
      "Lifetime access — no renewals",
      "Marketplace resale value · exclusive",
      "Cell health map · exclusive",
      "Up to 5 vehicles",
      "Every future feature at no extra cost",
      "Portable across your next EV",
      "Founding Member badge",
    ],
    cta: "Get Lifetime",
    plan: "lifetime",
    highlight: false,
    lifetime: true,
  },
];

export const FAQS = [
  {
    q: "Is EV IQ affiliated with Tesla?",
    a: "No. EV IQ is an independent third-party tool. We use Tesla's official Fleet API and OAuth flow, but we are not affiliated with, endorsed by, or sponsored by Tesla, Inc. Tesla, Model S, Model 3, Model X, and Model Y are trademarks of Tesla, Inc.",
  },
  {
    q: "A free BMS reader already shows my battery data — why pay for EVIQ?",
    a: "A free OBD/BMS reader gives you a snapshot: today's capacity, right now, on that one scan. EVIQ adds what a snapshot can't — a tracked degradation slope over months, a multi-year forecast against your exact warranty floor, trim-adjusted EPA baselining, fleet-relative ranking, and a cryptographically signed Battery Passport a buyer can verify. The reader gives you a number; EVIQ tells you what it means, where it's heading, and proves it to the next owner.",
  },
  {
    q: "What does Tesla's battery warranty actually cover?",
    a: "Tesla's Battery and Drive Unit Limited Warranty (US 2025) covers all Model S/3/X/Y/Cybertruck for 8 years and a model-specific mileage cap — 100,000 mi (Model 3 RWD, Model Y RWD/AWD), 120,000 mi (Model 3/Y Premium LR/Performance), or 150,000 mi (Model S, X, Cybertruck) — whichever comes first. Within that window, Tesla guarantees a minimum 70% capacity retention; below that, they're required to repair or replace at no cost. The original Roadster (2008–2012) is a special case at 2 yrs / 25,000 mi with no published retention guarantee. Sources: tesla.com/support/vehicle-warranty.",
  },
  {
    q: "What data does EV IQ access?",
    a: "Only read-only vehicle_device_data scope — battery level, charge state, range, and VIN for identification. We never request location, climate, charging controls, or any write permissions. Your token is encrypted at rest with AES-256-GCM.",
  },
  {
    q: "Can EV IQ open my car or drive it?",
    a: "No. EV IQ never requests command scopes. Even if we wanted to flash your lights, our token wouldn't let us. Read-only by design.",
  },
  {
    q: "How do you compute the health score?",
    a: "We compare your pack's current full-charge range capacity against the EPA-rated original for your VIN, normalized for ambient temperature and recent charge habits. We show the raw percentage, the trend, and the gap to Tesla's 70% warranty floor.",
  },
  {
    q: "How do I cancel?",
    a: "Cancel anytime from your account page. No phone calls, no retention forms. Your data is deleted within 30 days of cancellation unless you ask us to keep your history for a future return.",
  },
  {
    q: "How accurate is the data?",
    a: "EV IQ pulls directly from Tesla's Fleet API — the same source the Tesla mobile app uses. The numbers you see are the numbers your car reports. We compute trends and projections on top, but the raw measurements are first-party.",
  },
];

export const STATS = [
  { value: "1,284", label: "Tesla packs monitored" },
  { value: "$2.4M", label: "Warranty replacements unlocked" },
  { value: "70%", label: "The line we watch for you" },
  { value: "24/7", label: "Battery telemetry sync" },
];
